import { Delete, KeyboardArrowDown } from '@mui/icons-material';
import { ListItemIcon, ListItemText, Menu } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDeviceGroup } from '~/api/device-groups';
import { BulkActionButton, BulkActionMenuItem } from '~/components/bulk-actions';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { focusableDisabledItems } from '~/components/menu-item';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useBulkActionIds } from '~/hooks/unique-id';

export type BulkActionsProps = {
  selectedIds: GridRowSelectionModel;
  canDestroySelected: boolean;
};

export const BulkActions = ({ selectedIds, canDestroySelected }: BulkActionsProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { buttonId, menuId } = useBulkActionIds();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteDeviceGroup] = useDeleteDeviceGroup();

  if (!selectedIds.length) return null;

  return (
    <>
      <BulkActionButton
        ref={buttonRef}
        disableRipple
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </BulkActionButton>
      <Menu
        id={menuId}
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{
          ...focusableDisabledItems,
          'aria-labelledby': buttonId,
        }}
      >
        <BulkActionMenuItem
          tooltip={
            !canDestroySelected ? t('pages.device_groups.list.menu.cannot_delete_tooltip') : ''
          }
          disabled={!canDestroySelected}
          onClick={async () => {
            if (!canDestroySelected) return;
            setOpen(false);
            if (!(await confirmDelete())) return;
            await deleteDeviceGroup({
              variables: {
                input: { deviceGroupIds: selectedIds.map((x) => +x) },
              },
            });
          }}
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            {t('common.actions.delete')}
          </ListItemText>
        </BulkActionMenuItem>
      </Menu>

      <ConfirmDialog
        {...confirmDeleteProps}
        title={t('pages.device_groups.list.bulk_actions.delete_dialog_title')}
        prompt={t('pages.device_groups.list.bulk_actions.delete_dialog_prompt')}
        confirm={t('pages.device_groups.list.bulk_actions.delete_dialog_confirm')}
        deleteConfirm
      />
    </>
  );
};
